import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ButtonGroup } from '@atlaskit/button';
import Button from '@atlaskit/button/new';
import { Flex, xcss } from '@atlaskit/primitives';
import { useAnalytics } from '../Analytics/AnalyticsProvider';

const messages = defineMessages({
  accept: {
    id: 'footer.accept',
    defaultMessage: 'Accept',
    description:
      'Button to consent for this app to have the listed permissions',
  },
  cancel: {
    id: 'footer.cancel',
    defaultMessage: 'Cancel',
    description:
      'Button to cancel the consent for this app to have the listed permissions',
  },
});

const footerStyle = xcss({
  marginTop: 'space.400',
  marginBottom: 'space.400',
  justifyContent: 'end',
});

/** Variant without block margins, for embedding inside a sticky action bar. */
const embeddedFooterStyle = xcss({
  justifyContent: 'end',
});

type ButtonDecision = 'accept' | 'cancel';

export interface FooterProps {
  /** When true the Accept button is disabled regardless of click state. */
  acceptDisabled?: boolean;
  /**
   * When true, drop the default vertical margins so the buttons sit flush
   * inside a container that owns its own padding (e.g. the Rovo sticky bar).
   */
  embedded?: boolean;
  /**
   * When true, render Cancel before Accept (so the primary Accept sits on the
   * right). Used by the Rovo sticky action footer.
   */
  reverseOrder?: boolean;
}

export const Footer: FunctionComponent<FooterProps> = ({
  acceptDisabled = false,
  embedded = false,
  reverseOrder = false,
}) => {
  const analytics = useAnalytics();
  const [clickedButton, setClickedButton] = useState<ButtonDecision | null>(
    null,
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const form = wrapperRef.current?.closest('form');
    if (!form) return;
    const handleSubmit = (e: Event) => {
      if (e.defaultPrevented) {
        // Validation blocked the submit — re-enable buttons after the click handler's
        // own setTimeout has fired (which disables them).
        setTimeout(() => setClickedButton(null), 0);
      }
    };
    form.addEventListener('submit', handleSubmit);
    return () => form.removeEventListener('submit', handleSubmit);
  }, []);

  const fireAnalyticEvent = useCallback(
    (buttonName: string, buttonValue: ButtonDecision) => () => {
      analytics.sendButtonClickEvent(buttonName);
      setTimeout(() => setClickedButton(buttonValue), 0);
    },
    [analytics],
  );

  const acceptButton = (
    <Button
      type="submit"
      appearance="primary"
      name="decision"
      value="accept"
      onClick={fireAnalyticEvent('acceptConsent', 'accept')}
      // isLoading={clickedButton === 'accept'} // TODO: spinner doesn't render correctly
      isDisabled={acceptDisabled || clickedButton !== null}
    >
      <FormattedMessage {...messages.accept} />
    </Button>
  );

  const cancelButton = (
    <Button
      type="submit"
      name="decision"
      value="cancel"
      onClick={fireAnalyticEvent('cancelConsent', 'cancel')}
      // isLoading={clickedButton === 'cancel'} // TODO: spinner doesn't render correctly
      isDisabled={clickedButton !== null}
    >
      <FormattedMessage {...messages.cancel} />
    </Button>
  );

  return (
    <Flex xcss={embedded ? embeddedFooterStyle : footerStyle}>
      <ButtonGroup>
        {reverseOrder ? (
          <>
            {cancelButton}
            {acceptButton}
          </>
        ) : (
          <>
            {acceptButton}
            {cancelButton}
          </>
        )}
      </ButtonGroup>
    </Flex>
  );
};

export default Footer;
